// Updated VariantsSection.js with logic from ManageProduct
import React, { useEffect, useState } from "react";
import { Grid, Divider, Typography, Box, Button } from "@mui/material";
import VariantCard from "./variant-card";
import AddIcon from "@mui/icons-material/Add";
import { randomUid } from "../../utils/utility";

const VariantsSection = ({
  variants,
  setVariants,
  formErrors,
  setFormErrors,
}) => {
  const [expandedVariants, setExpandedVariants] = useState({ 0: true });

  useEffect(() => {
    if (variants?.length === 0) {
      let id = randomUid();
      setVariants([
        {
          price: "",
          weight: "",
          weight_unit: "",
          height: "",
          height_unit: "",
          width: "",
          width_unit: "",
          length: "",
          length_unit: "",
          sku: "",
          inventory: 0,
          hs_code: "",
          country_of_origin: "",
          hts_code: "",
          isBundle: false,
          isMaster: false,
          uid: id,
          parent: null,
          subSkus: [],
          title: "",
          option: [],
        },
      ]);
    }
  }, [variants, setVariants]);

  const handleToggleExpand = (index) => {
    setExpandedVariants((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...variants];
    updatedVariants[index][field] = value;
    setVariants(updatedVariants);
    setFormErrors((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        [`${index}_${field}`]: "",
      },
    }));
  };

  const handleBundleToggle = (variantIndex, isChecked, key = "isBundle") => {
    const updatedVariants = [...variants];
    updatedVariants[variantIndex][key] = isChecked;

    if (isChecked && updatedVariants[variantIndex].subSkus?.length === 0) {
      updatedVariants[variantIndex].subSkus = [
        {
          product: null,
          sku: null,
          price: "",
          hs_code: "",
          hts_code: "",
          country_of_origin: "",
          inventory: "",
          height: "",
          width: "",
          length: "",
          weight: "",
          height_unit: "",
          width_unit: "",
          length_unit: "",
          weight_unit: "",
          parent: updatedVariants[variantIndex].uid,
          uid: randomUid(),
          child: key === "isMaster",
          title: "",
        },
      ];
    } else {
      updatedVariants[variantIndex].subSkus = updatedVariants[
        variantIndex
      ].subSkus.map((x) => ({ ...x, child: isChecked && key === "isMaster" }));
    }

    setVariants(updatedVariants);
    //remove errors related to subSkus
    setFormErrors((prev) => ({
      ...prev,
      variants: Object.fromEntries(
        prev.variants
          ? Object.entries(prev.variants).filter(
              ([errorKey]) => !errorKey.includes(`${variantIndex}_subSku_`),
            )
          : [],
      ),
    }));
  };

  const handleAddSubSku = (variantIndex, master) => {
    const newVariants = [...variants];
    newVariants[variantIndex].subSkus.push({
      price: "",
      sku: "",
      hs_code: "",
      hts_code: "",
      country_of_origin: "",
      inventory: "",
      height: "",
      width: "",
      length: "",
      weight: "",
      height_unit: "",
      weight_unit: "",
      product: null,
      skuSelection: null,
      uid: randomUid(),
      parent: newVariants[variantIndex].uid,
      child: master,
      title: "",
    });
    setVariants(newVariants);
  };

  const handleRemoveSubSku = (variantIndex, subIndex) => {
    const newVariants = [...variants];
    newVariants[variantIndex].subSkus.splice(subIndex, 1);
    setVariants(newVariants);
    setFormErrors((prev) => ({
      ...prev,
      variants: Object.fromEntries(
        Object.entries(prev.variants).filter(
          ([errorKey]) =>
            !errorKey.startsWith(`${variantIndex}_subSku_${subIndex}_`),
        ),
      ),
    }));
  };

  const handleSubSkuChange = (variantIndex, subIndex, key, value) => {
    const newVariants = [...variants];
    newVariants[variantIndex].subSkus[subIndex][key] = value;
    setVariants(newVariants);
    setFormErrors((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        [`${variantIndex}_subSku_${subIndex}_${key}`]: "",
      },
    }));
  };

  const handleProductUpdate = (variantIndex, subIndex, product) => {
    const newVariants = [...variants];
    const subSku = newVariants[variantIndex].subSkus[subIndex];
    Object.assign(subSku, {
      product,
      price: "",
      sku: null,
      hs_code: "",
      hts_code: "",
      country_of_origin: "",
      inventory: "",
      height: "",
      width: "",
      length: "",
      weight: "",
      height_unit: "",
      weight_unit: "",
      title: "",
    });
    setVariants(newVariants);
    setFormErrors((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        [`${variantIndex}_subSku_${subIndex}_product`]: "",
      },
    }));
  };

  const updateSubSku = (variantIndex, subIndex, subSku, selectedSku) => {
    const product = subSku.product;
    if (product?.variants?.length > 0) {
      const matchedVariant = product.variants.find(
        (v) => v.sku === selectedSku?.sku,
      );
      const autoFilled = {
        price: matchedVariant?.price || "",
        weight: matchedVariant?.weight || "",
        weight_unit: matchedVariant?.weight_unit || "",
        height: matchedVariant?.height || "",
        height_unit: matchedVariant?.height_unit || "",
        width: matchedVariant?.width || "",
        width_unit: matchedVariant?.width_unit || "",
        length: matchedVariant?.length || "",
        length_unit: matchedVariant?.length_unit || "",
        hs_code: matchedVariant?.hs_code || "",
        hts_code: matchedVariant?.hts_code || "",
        country_of_origin: matchedVariant?.country_of_origin || "",
        inventory: matchedVariant?.inventory || 0,
        sku: selectedSku || "",
        title: matchedVariant?.title || "",
      };
      setVariants(
        variants.map((variant, index) => {
          if (index === variantIndex) {
            return {
              ...variant,
              subSkus: variant.subSkus.map((sub, si) =>
                si === subIndex ? { ...sub, ...autoFilled } : sub,
              ),
            };
          }
          return variant;
        }),
      );
      setFormErrors((prev) => ({
        ...prev,
        variants: {
          ...prev.variants,
          [`${variantIndex}_subSku_${subIndex}_sku`]: "",
          [`${variantIndex}_subSku_${subIndex}_title`]: "",
        },
      }));
    }
  };

  const onChangeDimensionAndWight = (index, field, value) => {
    setFormErrors((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        [`${index}_${field}`]: "",
      },
    }));
    if (
      ["weight", "height", "width", "length", "weight_unit"].includes(field)
    ) {
      handleVariantChange(index, field, value);
    } else if (field === "dimension_unit") {
      const updatedVariants = [...variants];
      updatedVariants[index]["height_unit"] = value;
      updatedVariants[index]["width_unit"] = value;
      updatedVariants[index]["length_unit"] = value;
      setVariants(updatedVariants);
    }
  };

  const onChangeSubDimensionAndWeight = (
    variantIndex,
    subIndex,
    field,
    value,
  ) => {
    const updatedVariants = [...variants];
    const subSku = updatedVariants[variantIndex].subSkus[subIndex];
    if (
      ["weight", "weight_unit", "height", "width", "length"].includes(field)
    ) {
      subSku[field] = value;

      // Validate dimensions for non-negative values
      if (["height", "width", "length", "weight"].includes(field)) {
        let err = "";
        if (value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
          err = `${field.charAt(0).toUpperCase() + field.slice(1)} must be > 0`;
        }
      }
    } else if (field === "dimension_unit") {
      subSku["height_unit"] = value;
      subSku["width_unit"] = value;
      subSku["length_unit"] = value;
    } else if (field === "price") {
      subSku[field] = value;
    }
    setVariants(updatedVariants);
    setFormErrors((prev) => ({
      ...prev,
      variants: {
        ...prev.variants,
        [`${variantIndex}_subSku_${subIndex}_${field}`]: "",
      },
    }));
  };

  const removeVariant = (indexToRemove) => {
    setVariants((prevVariants) =>
      prevVariants.filter((_, index) => index !== indexToRemove),
    );
    setFormErrors((prev) => ({
      ...prev,
      variants: Object.fromEntries(
        Object.entries(prev.variants).filter(
          ([errorKey]) => !errorKey.startsWith(`${indexToRemove}_`),
        ),
      ),
    }));
  };

  const addVariant = () => {
    let id = randomUid();
    setVariants((prevState) => [
      ...prevState,
      {
        price: "",
        weight: "",
        weight_unit: "",
        height: "",
        height_unit: "",
        width: "",
        width_unit: "",
        length: "",
        length_unit: "",
        sku: "",
        inventory: 0,
        hs_code: "",
        country_of_origin: "",
        hts_code: "",
        isBundle: false,
        isMaster: false,
        uid: id,
        parent: null,
        subSkus: [],
        title: "",
        key: [],
      },
    ]);
  };

  return (
    <Grid item xs={12} sm={12}>
      <Divider sx={{ color: "#979797", mt: 3 }} />
      <Typography
        fontFamily={"poppins"}
        fontWeight={400}
        fontSize={"20px"}
        textAlign={"left"}
        sx={{ color: (theme) => theme.palette.text.main }}
      >
        Variants
      </Typography>

      {variants?.map((variant, index) => (
        <VariantCard
          key={variant.uid}
          variant={variant}
          index={index}
          handleToggleExpand={handleToggleExpand}
          expanded={expandedVariants[index]}
          handleVariantChange={handleVariantChange}
          handleBundleToggle={handleBundleToggle}
          handleRemoveSubSku={handleRemoveSubSku}
          handleSubSkuChange={handleSubSkuChange}
          onChangeSubDimensionAndWeight={onChangeSubDimensionAndWeight}
          updateSubSku={updateSubSku}
          handleProductUpdate={handleProductUpdate}
          removeVariant={removeVariant}
          handleAddSubSku={handleAddSubSku}
          onChangeDimensionAndWight={onChangeDimensionAndWight}
          formErrors={formErrors.variants}
        />
      ))}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          onClick={addVariant}
          style={{
            textTransform: "none",
            color: (theme) => theme.palette.text.main,
          }}
        >
          <AddIcon fontSize="small" sx={{ color: "#0038FF" }} />
          <Typography
            fontFamily={"poppins"}
            fontSize={"14px"}
            fontWeight={400}
            sx={{ color: "#0038FF", marginLeft: "8px" }}
          >
            Add more Variants
          </Typography>
        </Button>
      </Box>
    </Grid>
  );
};

export default VariantsSection;
